import React, { Fragment } from "react"
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react"
import { Heading4 } from "./typography/headings"
import { BodyTextRegular } from "./typography/text"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"
import { Divider } from "./dividers/Divider"
import { ButtonNeutral } from "./button/ButtonNeutral"
import { translationKeys } from "../util/translationKeys"
import { useTranslation } from "react-i18next"
import { ButtonAction } from "./button/ButtonAction"
import { Badge } from "./badge/Badge"
import { ButtonTransparent } from "./button/ButtonTransparent"

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  isOpen: boolean
  onClose: () => void
  title?: string
  subtitle?: string
  subtitleSpan?: React.ReactNode
  onBackClick?: () => void
  showClose?: boolean
  onSubmit?: (() => void) | (() => Promise<void>)
  submitButtonText?: string
  closeButtonText?: string
  submitIsLoading?: boolean
  isForm?: boolean
  showCancelButton?: boolean
  showEscBadge?: boolean
}

// Cannot use DialogBackdrop from headlessui/react: https://github.com/tailwindlabs/headlessui/issues/2876
// Use when next version is released
export const DialogModal = ({
  isOpen,
  onClose,
  className,
  title,
  subtitle,
  onBackClick,
  children,
  showEscBadge = true,
  showClose = true,
  subtitleSpan,
  onSubmit,
  submitButtonText,
  closeButtonText,
  submitIsLoading,
  isForm = false,
  showCancelButton = true,
  ...rest
}: Props) => {
  const { t } = useTranslation()

  return (
    <Transition appear show={isOpen}>
      <Dialog
        as={isForm ? "form" : "div"}
        onClose={onClose}
        {...(isForm && onSubmit ? { onSubmit: onSubmit } : {})}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={`fixed inset-0 bg-black/20 z-top`} />
        </TransitionChild>
        <TransitionChild
          as={Fragment}
          enter="transition-opacity ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-out duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed z-top inset-0 flex w-screen items-center justify-center">
            <DialogPanel
              as="div"
              className={`flex flex-col rounded-2xl shadow-modal bg-white max-h-[95%] overflow-y-scroll max-w-[500px] w-full ${
                className ? className : ""
              }`}
              {...rest}
            >
              {title && (
                <div className="sticky top-0 z-floating bg-white">
                  <div
                    className={`flex flex-row ${
                      showClose ? "justify-between" : "justify-start"
                    } items-center  p-6 gap-2`}
                  >
                    <div className="flex flex-col gap-2 w-full h-full">
                      <DialogTitle as={Heading4} className="text-textPrimary">
                        {title}
                      </DialogTitle>
                      {(subtitle || subtitleSpan) && (
                        <BodyTextRegular className="text-textSecondary2">
                          {subtitle} {subtitleSpan}
                        </BodyTextRegular>
                      )}
                    </div>
                    {showClose && (
                      <div className="flex flex-row gap-2 items-center w-fit">
                        {showEscBadge && (
                          <Badge
                            className="bg-whiteHover border max-md:hidden border-greyBorder text-textPrimary"
                            badgeSize="medium"
                          >
                            esc
                          </Badge>
                        )}
                        <ButtonTransparent
                          leftIcon={<FontAwesomeIcon icon={solid("xmark")} />}
                          onClick={onClose}
                          type="button"
                          size="medium"
                        />
                      </div>
                    )}
                  </div>
                  <Divider className="bg-greyBorder" />
                </div>
              )}
              <div className="flex w-full flex-col">
                {children}
                {(onSubmit || showClose || onBackClick) && (
                  <div className="sticky bottom-0 bg-white">
                    <Divider className="bg-greyBorder" />
                    <div
                      className={`px-6 py-4 gap-4 flex flex-row ${
                        onBackClick ? "justify-between" : "justify-end"
                      }`}
                    >
                      {onBackClick && (
                        <ButtonNeutral
                          onClick={onBackClick}
                          size="large"
                          leftCenterIcon={
                            <FontAwesomeIcon icon={solid("arrow-left")} />
                          }
                        >
                          {t(translationKeys.comboOrderGoBack)}
                        </ButtonNeutral>
                      )}
                      <div className="flex h-full flex-row gap-2 items-center justify-end w-full">
                        {showCancelButton && (
                          <ButtonNeutral
                            className="h-[42px]"
                            onClick={onClose}
                            size="large"
                            type="button"
                          >
                            {closeButtonText ||
                              t(translationKeys.profileAnonymizeReqestCancel)}
                          </ButtonNeutral>
                        )}
                        {onSubmit && (
                          <ButtonAction
                            className="max-md:w-full"
                            onClick={onSubmit}
                            isLoading={submitIsLoading}
                            disabled={submitIsLoading}
                            type={isForm ? "submit" : "button"}
                            size="large"
                          >
                            {submitButtonText ||
                              t(translationKeys.comboProfileSave)}
                          </ButtonAction>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </DialogPanel>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  )
}
